import { useState, useCallback, memo, useMemo } from 'react';
import type {
  FlowArrow,
  WhiteboardElement,
  DeviceFrame,
  AnchorPosition,
} from '../../types/whiteboard';
import {
  getElementAbsolutePosition,
  getAnchorPoint,
  getFrameBoundingRect,
} from '../../types/whiteboard';

interface Props {
  arrows: FlowArrow[];
  elements: WhiteboardElement[];
  frames: DeviceFrame[];
  selectedArrowId: string | null;
  zoom: number;
  pan: { x: number; y: number };
  onSelectArrow: (id: string | null) => void;
  onUpdateArrow: (id: string, updates: Partial<FlowArrow>) => void;
  onDeleteArrow: (id: string) => void;
}

/** Get bounding rect for an entity (element or frame) */
function getEntityRect(
  entityId: string,
  elements: WhiteboardElement[],
  frames: DeviceFrame[]
): { x: number; y: number; width: number; height: number } | null {
  const el = elements.find(e => e.id === entityId);
  if (el) {
    const pos = getElementAbsolutePosition(el, frames);
    return { x: pos.x, y: pos.y, width: el.width, height: el.height };
  }
  const fr = frames.find(f => f.id === entityId);
  if (fr) {
    return getFrameBoundingRect(fr);
  }
  return null;
}

/** Generate SVG path for an arrow */
function getArrowPath(
  arrow: FlowArrow,
  elements: WhiteboardElement[],
  frames: DeviceFrame[]
): { path: string; labelPos: { x: number; y: number }; start: { x: number; y: number }; end: { x: number; y: number } } | null {
  const sourceRect = getEntityRect(arrow.sourceId, elements, frames);
  const targetRect = getEntityRect(arrow.targetId, elements, frames);
  if (!sourceRect || !targetRect) return null;

  const start = getAnchorPoint(arrow.sourceAnchor, sourceRect);
  const end = getAnchorPoint(arrow.targetAnchor, targetRect);

  let path: string;
  let labelPos: { x: number; y: number };

  switch (arrow.curveType) {
    case 'straight': {
      path = `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
      labelPos = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
      break;
    }

    case 'orthogonal': {
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;

      // Route based on anchor directions
      if (arrow.sourceAnchor === 'right' || arrow.sourceAnchor === 'left') {
        path = `M ${start.x} ${start.y} L ${midX} ${start.y} L ${midX} ${end.y} L ${end.x} ${end.y}`;
      } else {
        path = `M ${start.x} ${start.y} L ${start.x} ${midY} L ${end.x} ${midY} L ${end.x} ${end.y}`;
      }
      labelPos = { x: midX, y: midY };
      break;
    }

    case 'bezier':
    default: {
      const distance = Math.sqrt((end.x - start.x) ** 2 + (end.y - start.y) ** 2);
      const offset = Math.min(distance * 0.4, 150);

      const getControlPoint = (point: { x: number; y: number }, anchor: AnchorPosition, dist: number) => {
        switch (anchor) {
          case 'top': return { x: point.x, y: point.y - dist };
          case 'bottom': return { x: point.x, y: point.y + dist };
          case 'left': return { x: point.x - dist, y: point.y };
          case 'right': return { x: point.x + dist, y: point.y };
        }
      };

      const cp1 = getControlPoint(start, arrow.sourceAnchor, offset);
      const cp2 = getControlPoint(end, arrow.targetAnchor, offset);

      path = `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;
      // Label at midpoint of bezier (approximate)
      labelPos = {
        x: (start.x + cp1.x + cp2.x + end.x) / 4,
        y: (start.y + cp1.y + cp2.y + end.y) / 4,
      };
      break;
    }
  }

  return { path, labelPos, start, end };
}

export const FlowArrowLayer = memo(function FlowArrowLayer({
  arrows,
  elements,
  frames,
  selectedArrowId,
  zoom,
  pan,
  onSelectArrow,
  onUpdateArrow,
  onDeleteArrow: _onDeleteArrow,
}: Props) {
  const [hoveredArrowId, setHoveredArrowId] = useState<string | null>(null);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);

  const arrowPaths = useMemo(() => {
    return arrows.map(arrow => ({
      arrow,
      ...getArrowPath(arrow, elements, frames),
    })).filter(a => a.path !== undefined);
  }, [arrows, elements, frames]);

  const handleArrowClick = useCallback((e: React.MouseEvent, arrowId: string) => {
    e.stopPropagation();
    onSelectArrow(arrowId);
  }, [onSelectArrow]);

  const handleLabelDoubleClick = useCallback((e: React.MouseEvent, arrowId: string) => {
    e.stopPropagation();
    setEditingLabelId(arrowId);
  }, []);

  if (arrows.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 30 }}
    >
      {/* Arrowhead marker definition */}
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="8"
          refX="9"
          refY="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <polygon points="0 0, 10 4, 0 8" fill="var(--neutral-400)" />
        </marker>
        <marker
          id="arrowhead-active"
          markerWidth="10"
          markerHeight="8"
          refX="9"
          refY="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <polygon points="0 0, 10 4, 0 8" fill="var(--accent-600)" />
        </marker>
      </defs>

      <g
        transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}
      >
        {arrowPaths.map(({ arrow, path, labelPos, start, end }) => {
          if (!path || !labelPos || !start || !end) return null;

          const isSelected = selectedArrowId === arrow.id;
          const isHovered = hoveredArrowId === arrow.id;
          const isActive = isSelected || isHovered;
          const strokeColor = isActive ? 'var(--accent-600)' : (arrow.color || 'var(--neutral-400)');
          const strokeWidth = isActive ? 3 : 2;

          return (
            <g key={arrow.id}>
              {/* Invisible hit area (wider for easier clicking) */}
              <path
                d={path}
                fill="none"
                stroke="transparent"
                strokeWidth={12}
                className="pointer-events-auto cursor-pointer"
                onClick={(e) => handleArrowClick(e, arrow.id)}
                onMouseEnter={() => setHoveredArrowId(arrow.id)}
                onMouseLeave={() => setHoveredArrowId(null)}
              />

              {/* Visible arrow path */}
              <path
                d={path}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={arrow.style === 'dashed' ? '8 4' : 'none'}
                markerEnd={isActive ? 'url(#arrowhead-active)' : 'url(#arrowhead)'}
                className="transition-all duration-150"
              />

              {/* Endpoint circles when selected */}
              {isSelected && (
                <>
                  <circle
                    cx={start.x}
                    cy={start.y}
                    r={4}
                    fill="white"
                    stroke="var(--accent-600)"
                    strokeWidth={2}
                  />
                  <circle
                    cx={end.x}
                    cy={end.y}
                    r={4}
                    fill="white"
                    stroke="var(--accent-600)"
                    strokeWidth={2}
                  />
                </>
              )}

              {/* Label */}
              {(arrow.label || editingLabelId === arrow.id) && labelPos && (
                <foreignObject
                  x={labelPos.x - 50}
                  y={labelPos.y - 12}
                  width={100}
                  height={24}
                  className="pointer-events-auto"
                >
                  {editingLabelId === arrow.id ? (
                    <input
                      type="text"
                      value={arrow.label || ''}
                      onChange={(e) => onUpdateArrow(arrow.id, { label: e.target.value })}
                      onBlur={() => setEditingLabelId(null)}
                      onKeyDown={(e) => e.key === 'Enter' && setEditingLabelId(null)}
                      className="w-full text-center text-[10px] bg-surface border border-primary rounded px-1 py-0.5 outline-none"
                      autoFocus
                    />
                  ) : (
                    <div
                      className="w-full text-center cursor-pointer"
                      onDoubleClick={(e) => handleLabelDoubleClick(e, arrow.id)}
                    >
                      <span className="text-[10px] bg-surface/90 backdrop-blur-sm px-2 py-0.5 rounded text-muted border border-default">
                        {arrow.label}
                      </span>
                    </div>
                  )}
                </foreignObject>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
});
